import { cva, type VariantProps } from 'class-variance-authority';
import { LoaderCircleIcon } from 'lucide-react';
import { Slot as SlotPrimitive } from 'radix-ui';
import * as React from 'react';

import { cn } from '@/lib/utils';

const buttonVariants = cva(
    "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-xs font-sans text-sm font-medium transition-all cursor-pointer disabled:cursor-not-allowed disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
    {
        variants: {
            variant: {
                default:
                    'bg-primary text-primary-foreground hover:bg-primary/90 hover:shadow-sm active:scale-[0.98]',
                destructive:
                    'bg-destructive text-white hover:bg-destructive/90 hover:shadow-sm active:scale-[0.98] focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60',
                outline:
                    'border bg-background shadow-xs hover:bg-accent hover:text-accent-foreground hover:shadow-sm hover:border-ring active:scale-[0.98] dark:bg-input/30 dark:border-input dark:hover:bg-input/50',
                dashed: 'border-2 border-dashed border-primary bg-background text-primary shadow-xs hover:bg-accent hover:text-accent-foreground hover:shadow-sm active:scale-[0.98] dark:bg-input/30 dark:hover:bg-input/50',
                secondary:
                    'bg-secondary text-secondary-foreground hover:bg-secondary/80 hover:shadow-sm active:scale-[0.98]',
                ghost: 'hover:bg-black/3 hover:text-accent-foreground active:scale-[0.98] dark:hover:bg-white/3',
                link: 'text-primary underline-offset-4 hover:underline active:scale-[0.98]',
            },
            size: {
                default: 'h-9 px-4 py-2 has-[>svg]:px-3 w-full xs:w-auto',
                sm: 'h-8 rounded-xs gap-1.5 px-3 has-[>svg]:px-2.5 w-full xs:w-auto',
                lg: 'h-10 rounded-xs px-6 has-[>svg]:px-5 w-full xs:w-auto',
                icon: 'size-9',
                'icon-sm': 'size-8',
                'icon-lg': 'size-10',
            },
        },
        defaultVariants: {
            variant: 'default',
            size: 'default',
        },
    },
);

export type ButtonProps = React.ComponentProps<'button'> &
    VariantProps<typeof buttonVariants> & {
        icon?: React.ReactNode;
        loading?: boolean;
        asChild?: boolean;
        iconLabel?: string;
        hideOnDesktop?: boolean;
        hideOnMobile?: boolean;
    };

function Button({
    className,
    variant,
    size,
    icon,
    asChild = false,
    loading = false,
    children,
    iconLabel,
    disabled,
    hideOnDesktop,
    hideOnMobile,
    ...props
}: ButtonProps) {
    const Comp = asChild ? SlotPrimitive.Slot : 'button';

    return (
        <Comp
            data-slot="button"
            className={cn(
                buttonVariants({ variant, size }),
                hideOnMobile && 'desktop:flex hidden',
                hideOnDesktop && 'desktop:hidden',
                className,
            )}
            disabled={loading || disabled}
            {...props}
        >
            {loading && <LoaderCircleIcon className="animate-spin" />}
            {icon && !loading && icon}
            {iconLabel && !loading && (
                <span className="sr-only">{iconLabel}</span>
            )}
            {children}
        </Comp>
    );
}

export { Button, buttonVariants };
